import { FC, ReactNode, useEffect, useMemo, useState } from 'react';

import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { LettingTransactionsShowItem } from '@shared/apis/transactionsLettingsApi.types';
import { SaleTransactionsShowItem } from '@shared/apis/transactionsSalesApi.types';
// import { Button } from '@shared/components/button-new';
import { Carousel } from '@shared/components/carousel';
import { Tooltip } from '@shared/components/tooltip';
import { DEFAULT_PROPERTY_IMAGE_NEW } from '@shared/constants/noImage';
import { useAgencyConstants, useConfig } from '@shared/contexts/ConfigContext';
import { useCoreOptions } from '@shared/contexts/OptionsContext';
import { BuildingIcon } from '@shared/icons/building-2';
import { CheckIcon } from '@shared/icons/check';
import { ClockIcon } from '@shared/icons/clock';
import { CurrencyPoundIcon } from '@shared/icons/currency-pound';
// import { DownloadIcon } from '@shared/icons/download';
import { LogoShareIcon } from '@shared/icons/logo-share';
import { PenToolIcon } from '@shared/icons/pen-tool';
import { ShareIcon } from '@shared/icons/share-2';
import { ConfigAgencyConstants } from '@shared/models/config';
import { IImageDetails } from '@shared/models/IImages';
import { colours } from '@shared/theme/colours';
import { getImgixUrl } from '@shared/utils/common';
import { formatDate, PICKER_DATE_FORMAT } from '@shared/utils/date';
import { currencyString, getCurrencySymbol, processDecimal } from '@shared/utils/number';

import { styles } from './AtlasPublishOptionsPanel.styles';
import { FieldToggleList } from '../components/FieldToggleList';
import { FieldToggleItem } from '../components/FieldToggleList/FieldToggleList.types';

export interface AtlasPublishOptionsPanelProps extends WithStyles<typeof styles> {
  fieldList?: FieldToggleItem[];
  fieldVisibility?: Record<string, boolean>;
  transaction: SaleTransactionsShowItem | LettingTransactionsShowItem;
  onFieldVisibilityChange?: (fieldVisibility: Record<string, boolean>) => void;
  onUnpublish?: () => void;
}

interface BenefitItemProps {
  classes: {
    benefitItem: string;
    benefitText: string;
    checkIcon: string;
    checkIconWrapper: string;
  };
  text: string;
}

interface PreviewDetailRowProps {
  classes: {
    previewDetailRow: string;
    previewTooltipContainer: string;
    previewDetailIcon: string;
    previewDetailText: string;
  };
  icon: ReactNode;
  text: string;
  tooltip: string;
}

interface PreviewImageProps {
  classes: {
    completedBadge: string;
    completedBadgeText: string;
    previewImageContainer: string;
    previewImage: string;
    previewImagePlaceholder: string;
    previewImageIcon: string;
  };
  images: IImageDetails[];
}

const isLettingTransaction = (
  transaction: SaleTransactionsShowItem | LettingTransactionsShowItem
): transaction is LettingTransactionsShowItem => {
  return 'lease_start' in transaction;
};

const getTransactionDate = (transaction: SaleTransactionsShowItem | LettingTransactionsShowItem): string => {
  if ('completion_date' in transaction) {
    return formatDate(transaction.completion_date, PICKER_DATE_FORMAT) || '-';
  }
  return formatDate(transaction.signature_date, PICKER_DATE_FORMAT) || '-';
};

const formatLeaseDates = (transaction: LettingTransactionsShowItem): string => {
  if (!transaction.lease_start) return '-';
  return `${formatDate(transaction.lease_start, PICKER_DATE_FORMAT)} - ${formatDate(
    transaction.lease_end,
    PICKER_DATE_FORMAT
  )}`;
};

const formatTransactionPrice = (
  transaction: SaleTransactionsShowItem | LettingTransactionsShowItem,
  constants: ConfigAgencyConstants
): string => {
  if (isLettingTransaction(transaction) && transaction?.rent_psf && transaction?.rent_pa) {
    const rentPsf = processDecimal(transaction.rent_psf || 0, { decimalPlaces: 2 });
    const rentPa = currencyString(
      getCurrencySymbol(constants, transaction.rent_currency),
      transaction.rent_pa,
      constants.localisation?.currencyDefinitions[transaction.rent_currency]?.symbol_position
    );
    return `${rentPsf} ${t('psf_1')} - ${rentPa} ${t('per_annum')}`;
  }

  if (isLettingTransaction(transaction) && !transaction?.rent_psf && transaction?.rent_pa) {
    const rentPa = currencyString(
      getCurrencySymbol(constants, transaction.rent_currency),
      transaction.rent_pa,
      constants.localisation?.currencyDefinitions[transaction.rent_currency]?.symbol_position
    );
    return `${rentPa} ${t('per_annum')}`;
  }

  if (isLettingTransaction(transaction)) {
    return '-';
  }

  if (!transaction?.purchase_price) return '-';

  return currencyString(
    getCurrencySymbol(constants, transaction.purchase_price_currency),
    transaction.purchase_price,
    constants.localisation?.currencyDefinitions[transaction.purchase_price_currency]?.symbol_position
  );
};

const formatSizeString = (
  transaction: SaleTransactionsShowItem | LettingTransactionsShowItem,
  sizeMetricOptions: Array<{ id: string; label: string }>
): string => {
  const sizeValue = 'size_value' in transaction ? transaction.size_value : null;
  const sizeMetric = transaction.size_metric;

  if (!sizeValue || !sizeMetric) return '';

  const metricLabel = sizeMetricOptions.find((opt) => opt.id === sizeMetric)?.label || '';
  const formattedSize = typeof sizeValue === 'number' ? sizeValue.toLocaleString() : sizeValue;

  return metricLabel ? `${formattedSize} ${metricLabel}` : formattedSize;
};

const formatBuildingTypes = (
  transaction: SaleTransactionsShowItem | LettingTransactionsShowItem,
  buildingTypeOptions: Array<{ id: string; label: string }>,
  sizeMetricOptions: Array<{ id: string; label: string }>
): string => {
  const sizeText = formatSizeString(transaction, sizeMetricOptions);

  if (!transaction.building_types?.length) return sizeText || '-';

  const buildingTypesText = transaction.building_types
    .map((bt) => buildingTypeOptions.find((opt) => opt.id === bt)?.label || bt)
    .join(', ');

  return `${sizeText || ''} ${sizeText && buildingTypesText ? ' - ' : ''}${buildingTypesText || ''}`;
};

const BenefitItem: FC<BenefitItemProps> = ({ classes, text }) => (
  <div className={classes.benefitItem}>
    <div className={classes.checkIconWrapper}>
      <CheckIcon classes={{ root: classes.checkIcon }} color={colours.brandNavy[500]} />
    </div>
    <span className={classes.benefitText}>{text}</span>
  </div>
);

const PreviewDetailRow: FC<PreviewDetailRowProps> = ({ classes, icon, text, tooltip }) => (
  <div className={classes.previewDetailRow}>
    <Tooltip classes={{ container: classes.previewTooltipContainer }} title={tooltip}>
      <div className={classes.previewTooltipContainer}>{icon}</div>
    </Tooltip>
    <span className={classes.previewDetailText}>{text}</span>
  </div>
);

const PreviewImage: FC<PreviewImageProps> = ({ classes, images }) => (
  <div className={classes.previewImageContainer}>
    {images?.length > 1 ? (
      <Carousel
        classes={{ image: classes.previewImage }}
        images={images
          ?.slice(0, 5)
          .map((image) => ({ ...image, url: getImgixUrl(image.url, { w: 200, h: 190, fit: 'crop' }) }))}
      />
    ) : (
      <img
        src={getImgixUrl(images?.[0]?.url || DEFAULT_PROPERTY_IMAGE_NEW, { w: 200, h: 190, fit: 'crop' })}
        className={classes.previewImage}
        alt=""
      />
    )}
    <div className={classes.completedBadge}>
      <span className={classes.completedBadgeText}>{t('completed')}</span>
    </div>
  </div>
);

const AtlasPublishOptionsPanelComponent: FC<AtlasPublishOptionsPanelProps> = ({
  classes,
  transaction,
  fieldList,
  fieldVisibility: externalFieldVisibility,
  onFieldVisibilityChange,
  // onUnpublish,
}) => {
  const { buildingTypeOptions, availableSizeMetricOptions } = useCoreOptions();
  const { constants } = useAgencyConstants();
  const { user } = useConfig();

  const [fieldVisibility, setFieldVisibility] = useState<Record<string, boolean>>(() => {
    if (externalFieldVisibility && Object.keys(externalFieldVisibility).length > 0) {
      return externalFieldVisibility;
    }
    return (
      fieldList?.reduce((acc: Record<string, boolean>, field: FieldToggleItem) => {
        acc[field.key] = field.defaultVisible;
        return acc;
      }, {} as Record<string, boolean>) ?? {}
    );
  });

  useEffect(() => {
    if (externalFieldVisibility && Object.keys(externalFieldVisibility).length > 0) {
      setFieldVisibility(externalFieldVisibility);
    }
  }, [externalFieldVisibility]);

  const benefitItemClasses = useMemo(
    () => ({
      benefitItem: classes.benefitItem,
      checkIconWrapper: classes.checkIconWrapper,
      checkIcon: classes.checkIcon,
      benefitText: classes.benefitText,
    }),
    [classes]
  );

  const previewImageClasses = useMemo(
    () => ({
      previewImageContainer: classes.previewImageContainer,
      previewImage: classes.previewImage,
      previewImagePlaceholder: classes.previewImagePlaceholder,
      previewImageIcon: classes.previewImageIcon,
      completedBadge: classes.completedBadge,
      completedBadgeText: classes.completedBadgeText,
    }),
    [classes]
  );

  const previewDetailRowClasses = {
    previewDetailRow: classes.previewDetailRow,
    previewTooltipContainer: classes.previewTooltipContainer,
    previewDetailIcon: classes.previewDetailIcon,
    previewDetailText: classes.previewDetailText,
  };

  const transactionDisplayData = useMemo(() => {
    const result = {
      images: transaction?.images,
      transactionDate: getTransactionDate(transaction),
      leaseDates: isLettingTransaction(transaction) ? formatLeaseDates(transaction) : null,
      price: formatTransactionPrice(transaction, constants),
      buildingInfo: formatBuildingTypes(transaction, buildingTypeOptions, availableSizeMetricOptions),
    };
    return result;
  }, [transaction, constants, buildingTypeOptions, availableSizeMetricOptions]);

  const handleFieldToggle = (fieldKey: string, visible: boolean) => {
    setFieldVisibility((prev) => {
      const updated = {
        ...prev,
        [fieldKey]: visible,
      };
      onFieldVisibilityChange?.(updated);
      return updated;
    });
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.headerTitleContainer}>
          <div className={classes.logoShareIcon}>
            <LogoShareIcon />
          </div>
          <div className={classes.headerTitleRow}>
            <ShareIcon classes={{ root: classes.headerIcon }} color="#7E8899" />
            <h2 className={classes.title}>{t('share_your_success_to_the_atlas_community')}</h2>
          </div>
        </div>
        <div className={classes.benefitsList}>
          <BenefitItem classes={benefitItemClasses} text={t('could_boost_your_atlas_rankings')} />
          <BenefitItem classes={benefitItemClasses} text={t('instant_visibility_across_the_community')} />
          <BenefitItem classes={benefitItemClasses} text={t('helps_meet_your_organisation_s_obligations')} />
        </div>
      </div>

      <div className={classes.content}>
        <div className={classes.previewCard}>
          <PreviewImage classes={previewImageClasses} images={transactionDisplayData.images} />
          <div className={classes.previewContentContainer}>
            <div className={classes.previewTitleRow}>
              <span className={classes.previewAddress}>{transaction.primary_address}</span>
            </div>
            <span className={classes.previewOrganisationInfo}>{user?.organisation?.name} </span>
            <div className={classes.previewDetails}>
              <PreviewDetailRow
                classes={previewDetailRowClasses}
                icon={<PenToolIcon classes={{ root: classes.previewDetailIcon }} color={colours.neutral[400]} />}
                text={transactionDisplayData.transactionDate}
                tooltip={t('transaction_date')}
              />
              {isLettingTransaction(transaction) && transactionDisplayData.leaseDates && (
                <PreviewDetailRow
                  classes={previewDetailRowClasses}
                  icon={<ClockIcon classes={{ root: classes.previewDetailIcon }} color={colours.neutral[400]} />}
                  text={transactionDisplayData.leaseDates}
                  tooltip={t('lease_start_or_end_date_1')}
                />
              )}
              <PreviewDetailRow
                classes={previewDetailRowClasses}
                icon={<CurrencyPoundIcon classes={{ root: classes.previewDetailIcon }} color={colours.neutral[400]} />}
                text={transactionDisplayData.price}
                tooltip={
                  isLettingTransaction(transaction) ? t('achieved_rent_per_sq_ft_or_per_annum_1') : t('purchase_price')
                }
              />
              <PreviewDetailRow
                classes={previewDetailRowClasses}
                icon={<BuildingIcon classes={{ root: classes.previewDetailIcon }} color={colours.neutral[400]} />}
                text={transactionDisplayData.buildingInfo}
                tooltip={t('size_slash_property_type')}
              />
            </div>
          </div>
        </div>
        <div className={classes.fieldToggleSection}>
          <FieldToggleList
            classes={{ root: classes.fieldToggleList }}
            fieldVisibility={fieldVisibility}
            onFieldToggle={handleFieldToggle}
            fieldList={fieldList}
          />
        </div>
        {/* Temporarily removed unpublish option */}
        {/* {transaction?.publish_to_atlas?.published && (
          <div className={classes.unpublishSection}>
            <div className={classes.unpublishHeader}>
              <DownloadIcon classes={{ root: classes.unpublishIcon }} />
              <span className={classes.unpublishText}>
                {t('unpublish_record_and_data_from_atlas', "Unpublish this record and it's data from Atlas")}
              </span>
            </div>
            <Button
              text={t('unpublish_now', 'Unpublish now')}
              variant="outlined"
              classes={{ root: classes.unpublishButton }}
              onClick={onUnpublish}
            />
          </div>
        )} */}
      </div>
    </div>
  );
};

export const AtlasPublishOptionsPanel = withStyles(styles)(AtlasPublishOptionsPanelComponent);
